import { VALID_FILE_TYPES } from '@bybeam/platform-types';
import { TemporaryNewDocument } from '@platform-ui/@types/document.js';
import cx from 'classnames';
import { ChangeEvent, ReactNode } from 'react';
import sanitize from 'sanitize-filename';
import { validateFileName } from '../utils/validateFilename.js';
import styles from './UnstyledButton.module.css';

interface UnstyledButtonProps {
  children: ReactNode;
  className: string;
  disabled?: boolean;
  hasError?: boolean;
  helperTextId?: string;
  id: string;
  multiple?: boolean;
  onUploadDocuments?: (docs: TemporaryNewDocument[]) => void;
  onUploadError?: (errorMessage: string | ReactNode) => void;
}

export default function UnstyledButton({
  children,
  className,
  disabled,
  hasError = false,
  helperTextId,
  id,
  multiple,
  onUploadDocuments,
  onUploadError,
}: UnstyledButtonProps): JSX.Element {
  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0 || !onUploadDocuments) return;

    const rawDocuments = e.target.files;
    if (rawDocuments.length > 100) {
      onUploadError?.(
        <>
          <strong>Document upload failed:</strong> You can upload a max of 100 documents at a time.
          Please try again.
        </>,
      );
      return;
    }

    const filesHaveError = validateFileName(rawDocuments);
    if (filesHaveError) {
      onUploadError?.(
        <>
          <strong>Document upload failed:</strong> {filesHaveError}
        </>,
      );
      return;
    }

    const formattedDocuments: TemporaryNewDocument[] = Array.from(rawDocuments).map((file) => ({
      file,
      url: `${URL.createObjectURL(file)}`,
      name: sanitize(file.name),
      mimetype: file.type,
      isNew: true,
    }));
    onUploadDocuments(formattedDocuments);
    e.target.value = null;
  };

  const acceptedTypes = VALID_FILE_TYPES.map((type) => `.${type}`).join(',');

  return (
    <label
      htmlFor={`upload-${id}`}
      className={cx(
        className,
        'focus-within:outline focus-within:outline-2 focus-within:outline-firefoxDefaultFocus focus-within:outline-webkitDefaultFocus',
      )}
    >
      <input
        id={`upload-${id}`}
        type="file"
        accept={acceptedTypes}
        className={`${styles.UnstyledButton} w-0 h-0`}
        multiple={multiple}
        disabled={disabled}
        onChange={onChange}
        aria-errormessage={helperTextId}
        aria-invalid={hasError}
      />
      {children}
    </label>
  );
}
